import React from 'react';

import styled from 'styled-components';

import {ConfirmFormPopover} from 'app/general/widgets/ConfirmPopover';
import {useDeleteLane} from 'app/lanes/api';
import {useProcedures} from 'app/procedures/api';
import {useProcesses} from 'app/process/api';
import {gt} from 'app/utils/i18n';

interface Props {
  isOpen: boolean;
  toggle: () => unknown;
  target: HTMLElement;
  laneHashid: string;
}

export function ConfirmLaneDeletePopover({
  isOpen,
  toggle,
  target,
  laneHashid,
}: Props): JSX.Element {
  const [deleteLane] = useDeleteLane();
  const {data: procedures, isError: isProceduresError} = useProcedures({
    lane: laneHashid,
  });
  const {data: processes, isError: isProcessesError} = useProcesses({
    lane: laneHashid,
  });

  const state: 'error' | 'loading' | 'empty' | 'ready' = React.useMemo(() => {
    if (procedures == null || processes == null) {
      return isProceduresError || isProcessesError ? 'error' : 'loading';
    }
    return procedures.results.length == 0 && processes.results.length === 0
      ? 'empty'
      : 'ready';
  }, [
    procedures?.results.length,
    processes?.results.length,
    isProceduresError,
    isProcessesError,
  ]);

  return (
    <ConfirmFormPopover
      overlayProps={{
        target,
        isOpen,
        toggle,
        placement: 'left',
      }}
      text={
        <div>
          <div className="font-weight-bold">
            {gt('Are you sure you want to move this lane the Recycle Bin?')}
          </div>
          <div className="my-2">
            {state === 'error' ? (
              <span className="text-danger">
                {gt("Couldn't check lane usage at the moment.")}
              </span>
            ) : state === 'loading' ? (
              <>
                <i className="fa fa-circle-notch fa-spin mr-1" />
                {gt('Checking lane usage...')}
              </>
            ) : state === 'empty' ? (
              gt('This lane is not used in any document.')
            ) : (
              gt('These documents are currently using this lane:')
            )}
          </div>
          {state === 'ready' ? (
            <StyledListContainerDiv>
              {[
                {title: gt('Procedures'), items: procedures},
                {title: gt('Processes'), items: processes},
              ].map(({title, items}) =>
                items?.count ?? 0 > 0 ? (
                  <React.Fragment key={title}>
                    <div className="border-bottom font-weight-bold">
                      {title}
                    </div>
                    <ul className="pl-4">
                      {items?.results.map((procedure) => (
                        <li className="my-1" key={procedure.id}>
                          <a
                            href={procedure.html_url}
                            target="_blank"
                            rel="noreferrer"
                          >
                            {procedure.name}
                          </a>
                        </li>
                      ))}
                    </ul>
                  </React.Fragment>
                ) : null
              )}
            </StyledListContainerDiv>
          ) : null}
        </div>
      }
      confirmText={gt('Move to Recycle Bin')}
      onConfirm={() => deleteLane(laneHashid).unwrap()}
      isIdle={true}
    />
  );
}

const StyledListContainerDiv = styled.div`
  font-size: 90%;
  max-height: 70vh;
  overflow-y: auto;
`;
